// EventCard.js with modifications to restore card border
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useLanguage } from '../../../../contexts/LanguageContext';
import EventDetails from './EventDetails';
import EventAttendees from './EventAttendees';
import EventSetlist from './EventSetlist';
import EventComments from './EventComments';
import EventDetailsModal from '../event_modal/EventDetailsModal';
import EventAttendeesModal from '../event_modal/EventAttendeesModal';
import EventSetlistModal from '../event_modal/EventSetlistModal';
import HeaderImageModal from '../../mobile/components/modals/HeaderImageModal';
import PermissionAwareEditButton from '../../../PermissionAwareEditButton';
import { fetchEventById, deleteEvent, exportEventSetlistPPTX } from '../../../../services/eventService';
import { fetchSetlistById } from '../../../../services/setlistService';
import { fetchSongById } from '../../../../services/songService';
import { getCommunity } from '../../../../services/communityService';
import { fixImageUrl } from '../../../../services/imageService'; // Import the fixImageUrl utility
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faEye, faUsers, faTrash, faChevronDown, faChevronUp, faVideo, faImage, faFileExport } from '@fortawesome/free-solid-svg-icons';
import { Button } from 'react-bootstrap';
import LikeButton from '../../../LikeButton';
import './EventCard.css';

// Custom wrapper for PermissionAwareEditButton that uses trash icon instead of pencil
const DeleteButton = ({ item, onClick, color="text-secondary", title, ...rest }) => {
    const { user, userCommunities } = useAuth();
    
    // Check if user can edit this item - simplified version of the logic in PermissionAwareEditButton
    const canEdit = () => {
        if (!item || !user) return false;
        
        // Global admins can edit anything
        if (user.is_global_admin) return true;
        
        // Item owners can edit their own items
        if (user.id === item.owner_id) return true;
        
        // For community items, check membership
        if (item.community_id && user.isAdminOfCommunity && user.isAdminOfCommunity(item.community_id)) {
            return true;
        }
        
        return false;
    };
    
    // If user doesn't have permission, render nothing
    if (!canEdit()) {
        return null;
    }
    
    // Render a simple button with trash icon
    return (
        <button 
            className={`btn btn-link ${color} p-0 ms-2`}
            onClick={onClick}
            title={title}
            {...rest}
        >
            <FontAwesomeIcon icon={faTrash} />
        </button>
    );
};

// Predefined list of gradients
const EVENT_GRADIENTS = [
    'linear-gradient(135deg, #355C7D, #6C5B7B)',  /* Deep blue to rich violet */
    'linear-gradient(135deg, #414345, #232526)',  /* Dark gray gradients */
    'linear-gradient(135deg, #283048, #859398)',  /* Dark slate to muted gray */
    'linear-gradient(135deg, #373B44, #4286f4)',  /* Charcoal gray to vibrant blue */
    'linear-gradient(135deg, #200122, #6f0000)',  /* Dark plum to deep maroon */
    'linear-gradient(135deg, #5433FF, #20BDFF)',  /* Deep purple to vivid cyan */
    'linear-gradient(135deg, #2C3E50, #4CA1AF)',  /* Midnight blue to teal */
    'linear-gradient(135deg, #4568DC, #B06AB3)',  /* Strong blue to rich purple */
    'linear-gradient(135deg, #41295a, #2F0743)',  /* Dark purple shades */
    'linear-gradient(135deg, #093028, #237A57)',  /* Deep green shades */
    'linear-gradient(135deg, #6441A5, #2a0845)',  /* Rich purple to dark indigo */
    'linear-gradient(135deg, #42275a, #734b6d)',  /* Dark violet to mauve */
    'linear-gradient(135deg, #141E30, #243B55)',  /* Navy blue gradients */
    'linear-gradient(135deg, #870000, #190A05)',  /* Deep red to dark chocolate */
    'linear-gradient(135deg, #232526, #414345)'   /* Subtle dark grays */
];

// Select a gradient from the list based on event ID
const getEventGradientFromList = (eventId) => {
    // If no eventId is provided, fallback to random gradient
    if (!eventId) {
        const randomIndex = Math.floor(Math.random() * EVENT_GRADIENTS.length);
        return EVENT_GRADIENTS[randomIndex];
    }
    
    // Convert eventId to a number if it's a string
    const numericId = typeof eventId === 'string' ? 
        parseInt(eventId.replace(/[^0-9]/g, ''), 10) : 
        Number(eventId);
    
    // Use modulo to select a gradient from the list based on event ID
    const index = numericId % EVENT_GRADIENTS.length;
    return EVENT_GRADIENTS[index];
};

// Keep the original random function for fallback
const getRandomGradient = () => {
    // Generate two random colors for the gradient
    const generateRandomColor = () => {
        const hue = Math.floor(Math.random() * 360); // Random hue (0-359)
        const saturation = 60 + Math.floor(Math.random() * 30); // Saturation between 60-90%
        const lightness = 30 + Math.floor(Math.random() * 20); // Lightness between 30-50%
        return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    };
    
    const color1 = generateRandomColor();
    const color2 = generateRandomColor();
    
    // Create a linear gradient with the random colors
    return `linear-gradient(135deg, ${color1}, ${color2})`;
};

// Default background gradients - fallback for when no event ID exists
const DEFAULT_BG = getRandomGradient();

function EventCard({ event, onEdit, onRefresh, onDelete }) {
    const { t } = useLanguage();

    // Format visibility for display
    const formatVisibility = (visibility) => {
        if (!visibility) return t('events.visibility.default');

        switch(visibility.toLowerCase()) {
            case 'public':
                return t('events.visibility.public');
            case 'private':
                return t('events.visibility.private');
            case 'community':
                return t('events.visibility.community');
            default:
                return visibility;
        }
    };
    const [eventData, setEventData] = useState(event);
    const [setlist, setSetlist] = useState(null);
    const [loading, setLoading] = useState(false);
    const [communityName, setCommunityName] = useState('');
    const [backgroundStyle, setBackgroundStyle] = useState({
        backgroundImage: DEFAULT_BG,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '200px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '1.5rem'
    });
    const [imageLoading, setImageLoading] = useState(false);
    const { user } = useAuth();
    
    // State for song part information
    const [songsWithParts, setSongsWithParts] = useState({});
    const [loadingSongParts, setLoadingSongParts] = useState(false);
    
    // State for modals
    const [showEventModal, setShowEventModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showAttendeesModal, setShowAttendeesModal] = useState(false);
    const [showSetlistModal, setShowSetlistModal] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [showHeaderImageModal, setShowHeaderImageModal] = useState(false);
    
    // State for collapsing attendees section
    const [attendeesCollapsed, setAttendeesCollapsed] = useState(true);

    // State for collapsing comments section
    const [commentsCollapsed, setCommentsCollapsed] = useState(true);

    // State for header image
    const [headerImageLoaded, setHeaderImageLoaded] = useState(false);

    // State for PPTX export
    const [exportingPPTX, setExportingPPTX] = useState(false);

    // Check if this is a party event (Zabava)
    const isPartyEvent = eventData && eventData.event_type === 'party';

    // Update background style when event data changes
    useEffect(() => {
        if (!eventData) return;
        
        // If event has a header image, use it as background
        if (eventData.has_header_image) {
            setImageLoading(true);
            
            // Create the image URL using the fixImageUrl utility
            const headerImageUrl = fixImageUrl(`/api/events/${eventData.id}/header-image`);
            
            // Create a new Image to preload
            const img = new Image();
            img.onload = () => {
                setBackgroundStyle({
                    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.7)), url(${headerImageUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    minHeight: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    padding: '1.5rem'
                });
                setImageLoading(false);
                setHeaderImageLoaded(true);
            };
            
            img.onerror = () => {
                // Fall back to gradient background
                const gradient = eventData.id ? 
                    getEventGradientFromList(eventData.id) : 
                    getRandomGradient();
                
                setBackgroundStyle(prev => ({
                    ...prev,
                    backgroundImage: gradient
                }));
                setImageLoading(false);
                setHeaderImageLoaded(false);
            };
            
            img.src = headerImageUrl;
        } else {
            // Use gradient background
            const gradient = eventData.id ? 
                getEventGradientFromList(eventData.id) : 
                getRandomGradient();
                
            setBackgroundStyle(prev => ({
                ...prev,
                backgroundImage: gradient
            }));
            setHeaderImageLoaded(false);
        }
    }, [eventData?.id, eventData?.has_header_image]); // Dependencies include header image property

    // Fetch community name if not already present
    useEffect(() => {
        const loadCommunityName = async () => {
            if (!eventData?.community_id) return;
            if (eventData?.community_name) {
                setCommunityName(eventData.community_name);
                return;
            }
            
            try {
                // This would depend on how your community service is set up
                // You might need to implement fetchCommunityById in communityService.js
                const communityData = await getCommunity(eventData.community_id);
                if (communityData && communityData.name) {
                    setCommunityName(communityData.name);
                    // Update eventData with community name for consistency
                    setEventData(prevData => ({
                        ...prevData,
                        community_name: communityData.name
                    }));
                }
            } catch (error) {
                // Error fetching community name
            }
        };
        
        loadCommunityName();
    }, [eventData?.community_id, eventData?.community_name]);

    // Update eventData when event prop changes
    useEffect(() => {
        setEventData(event);
        // Reset setlist when event changes to prevent showing previous event's setlist
        setSetlist(null);
        // Reset song parts data
        setSongsWithParts({});
    }, [event]);

    // Load event data and setlist when component mounts or refreshes or event changes
    useEffect(() => {
        const loadEventData = async () => {
            if (!eventData?.id) return;
            
            setLoading(true);
            try {
                // Fetch the full event data including attendees
                const fullEventData = await fetchEventById(eventData.id, { includeAttendees: true });
                setEventData(fullEventData);
                
                // If the event has a community_id but no community_name, fetch the community info
                if (fullEventData.community_id && !fullEventData.community_name) {
                    try {
                        const communityData = await getCommunity(fullEventData.community_id);
                        if (communityData && communityData.name) {
                            setCommunityName(communityData.name);
                            // Update the event data with the community name
                            fullEventData.community_name = communityData.name;
                        }
                    } catch (communityError) {
                        // Error fetching community data
                    }
                }
                
                // If event has a setlist and is not a party event, fetch the setlist
                if (fullEventData.setlist_id && fullEventData.event_type !== 'party') {
                    const setlistData = await fetchSetlistById(fullEventData.setlist_id);
                    setSetlist(setlistData);
                    
                    // After fetching setlist, fetch song details for part information
                    if (setlistData && setlistData.songs && setlistData.songs.length > 0) {
                        fetchSongPartsData(setlistData.songs);
                    }
                } else {
                    // Clear setlist if the new event doesn't have one or is a party event
                    setSetlist(null);
                    setSongsWithParts({});
                }
            } catch (error) {
                // Error loading event data
            } finally {
                setLoading(false);
            }
        };

        loadEventData();
    }, [eventData?.id, onRefresh]);
    
    // Function to fetch song part information
    const fetchSongPartsData = async (songs) => {
        // Skip for party events
        if (isPartyEvent) return;
        
        if (!songs || songs.length === 0) return;
        
        setLoadingSongParts(true);
        try {
            const songPartsData = {};
            
            // Fetch each song to get part file information
            for (const song of songs) {
                if (!song.song_id) continue;
                
                try {
                    const songData = await fetchSongById(song.song_id);

                    if (songData) {
                        // Store the song data with proper ID mapping for SongPartPreview
                        songPartsData[song.song_id] = {
                            ...song,           // Include setlist song info (song_role, etc)
                            ...songData        // Add all the song data from the API
                            // Important: We DON'T set id here - we'll do it in EventSetlist when needed
                        };
                    }
                } catch (songError) {
                    // Error fetching individual song
                }
            }

            setSongsWithParts(songPartsData);
        } catch (error) {
            // Error fetching song part information
        } finally {
            setLoadingSongParts(false);
        }
    };

    // Update parent component with new event data
    const handleEventDataUpdate = (updatedData) => {
        setEventData(updatedData);
    };

    // Handle the save actions for the section modals
    const handleDetailsSaved = async () => {
        // Refresh the event data
        try {
            const updatedEvent = await fetchEventById(eventData.id, { includeAttendees: true });
            setEventData(updatedEvent);
            setShowDetailsModal(false);
        } catch (error) {
            // Error refreshing event details
        }
    };

    const handleAttendeesSaved = async () => {
        // Refresh the event data with updated attendees
        try {
            const updatedEvent = await fetchEventById(eventData.id, { includeAttendees: true });
            setEventData(updatedEvent);
            setShowAttendeesModal(false);
        } catch (error) {
            // Error refreshing attendees
        }
    };

    const handleSetlistSaved = async () => {
        // Skip for party events
        if (isPartyEvent) {
            setShowSetlistModal(false);
            return;
        }

        // Refresh the setlist data
        try {
            if (eventData.setlist_id) {
                const updatedSetlist = await fetchSetlistById(eventData.setlist_id);
                setSetlist(updatedSetlist);

                // After fetching updated setlist, refresh song part information
                if (updatedSetlist && updatedSetlist.songs && updatedSetlist.songs.length > 0) {
                    fetchSongPartsData(updatedSetlist.songs);
                }
            } else {
                // If the event now has a setlist ID (was added in the modal)
                const updatedEvent = await fetchEventById(eventData.id);
                setEventData(updatedEvent);

                if (updatedEvent.setlist_id) {
                    const updatedSetlist = await fetchSetlistById(updatedEvent.setlist_id);
                    setSetlist(updatedSetlist);

                    // Refresh song part information for the new setlist
                    if (updatedSetlist && updatedSetlist.songs && updatedSetlist.songs.length > 0) {
                        fetchSongPartsData(updatedSetlist.songs);
                    }
                }
            }
            setShowSetlistModal(false);
        } catch (error) {
            // Error refreshing setlist
        }
    };

    const handleHeaderImageUpdate = (updatedEvent) => {
        // Update the event data with the new header image info
        setEventData(updatedEvent);
        setShowHeaderImageModal(false);
    };

    // Handle delete event
    const handleDeleteEvent = async () => {
        try {
            // Call the deleteEvent function from eventService
            await deleteEvent(eventData.id);

            // Close the confirmation modal
            setShowConfirmDelete(false);

            // IMPORTANT: Call the parent's onDelete handler if provided
            // This will update the parent component's state to remove the deleted event
            if (onDelete) {
                onDelete(eventData.id);
            } else {
                // If no onDelete handler provided, refresh the parent component
                if (onRefresh) {
                    onRefresh();
                }
            }
        } catch (error) {
            alert(t('events.messages.errorDeletingTryAgain'));
        }
    };

    // Handle PPTX export
    const handleExportPPTX = async () => {
        if (!eventData || !eventData.id) return;

        setExportingPPTX(true);
        try {
            // Call the export API
            const blob = await exportEventSetlistPPTX(eventData.id);

            // Create a download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Generate filename
            const eventTitle = eventData.title || 'Event';
            const safeTitle = eventTitle.replace(/[^a-z0-9]/gi, '_');
            link.download = `${safeTitle}_setlist.pptx`;

            // Trigger download
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting PPTX:', error);
            alert(t('events.setlist.exportError') || 'Error exporting presentation. Please try again.');
        } finally {
            setExportingPPTX(false);
        }
    };

    // Only render if we have event data
    if (!eventData) {
        return (
            <div className="card shadow-sm border rounded-4 mb-3">
                <div className="card-body text-center p-3">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">{t('common.loading')}</span>
                    </div>
                    <p className="mt-2 mb-0 small">{t('events.messages.loadingEventData')}</p>
                </div>
            </div>
        );
    }

    // Create optimized styles for desktop
    const getHeaderStyle = () => {
        if (imageLoading) {
            return {
                backgroundImage: 'none',
                backgroundColor: '#343a40',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                minHeight: '200px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                padding: '1.5rem'
            };
        }
        return backgroundStyle;
    };

    // Format date for display
    const formatDate = (dateStr) => {
        if (!dateStr) return t('events.details.dateNotSet');
        const date = new Date(dateStr);
        return date.toLocaleDateString('sl-SI', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Combine community name from state or event data
    const displayCommunityName = eventData.community_name || communityName;

    // Delete confirmation modal
    const DeleteConfirmationModal = () => {
        if (!showConfirmDelete) return null;

        return (
            <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{t('events.messages.deleteConfirmTitle')}</h5>
                            <button type="button" className="btn-close" onClick={() => setShowConfirmDelete(false)}></button>
                        </div>
                        <div className="modal-body">
                            <p>{t('events.messages.deleteConfirmMessage', { title: eventData.title })}</p>
                            <p className="text-danger"><strong>{t('events.messages.deleteWarning')}</strong></p>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={() => setShowConfirmDelete(false)}>{t('events.actions.cancel')}</button>
                            <button type="button" className="btn btn-danger" onClick={handleDeleteEvent}>{t('events.actions.deleteEvent')}</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="card shadow-sm border rounded-4 mb-3"> {/* Changed from border-0 to border */}
            {/* Optimized Event Header with Details included */}
            <div
                className="card-header border-0"
                style={{ ...getHeaderStyle(), position: 'relative' }}
            >
                {imageLoading && (
                    <div className="text-center py-5">
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-white" />
                    </div>
                )}
                
                {!imageLoading && (
                    <>
                        {/* Top section with date as main title */}
                        <div className="d-flex justify-content-between align-items-center w-100 mb-1">
                            <h4 className="mb-0 text-white">
                                {formatDate(eventData.date)}
                            </h4>
                            
                            {/* Actions area with Visibility indicator, Recording link, Header image, and Delete button */}
                            <div className="d-flex align-items-center">
                                {eventData.visibility && (
                                    <div className="badge bg-dark bg-opacity-50 text-white me-2">
                                        <FontAwesomeIcon icon={faEye} className="me-1" />
                                        {formatVisibility(eventData.visibility)}
                                    </div>
                                )}

                                {/* Recording link button */}
                                {eventData.youtube_link && (
                                    <button
                                        className="btn btn-link text-white p-0 me-2"
                                        onClick={() => window.open(eventData.youtube_link, '_blank', 'noopener,noreferrer')}
                                        title={t('events.actions.openRecording')}
                                        style={{ fontSize: '1.2rem' }}
                                    >
                                        <FontAwesomeIcon icon={faVideo} />
                                    </button>
                                )}

                                {/* Header image button - wrapped with permission check */}
                                <PermissionAwareEditButton
                                    item={eventData}
                                    onClick={() => setShowHeaderImageModal(true)}
                                    color="text-white"
                                    title={t('events.actions.editHeaderImage') || 'Edit header image'}
                                    icon={faImage}
                                />

                                {/* Delete button - using a wrapper component */}
                                <DeleteButton
                                    item={eventData}
                                    onClick={() => setShowConfirmDelete(true)}
                                    color="text-white"
                                    title={t('events.actions.deleteEvent')}
                                />
                            </div>
                        </div>
                        
                        {/* Event title and community info */}
                        <div className="mb-2">
                            <h5 className="text-white">
                                {eventData.title}
                            </h5>
                            {displayCommunityName && (
                                <div className="badge bg-dark bg-opacity-50 text-white mt-1">
                                    <FontAwesomeIcon icon={faUsers} className="me-1" />
                                    {displayCommunityName}
                                </div>
                            )}
                        </div>
                        
                        {/* Like button — bottom-right corner */}
                        <div className="d-flex justify-content-end mb-1">
                            <LikeButton
                                contentType="event"
                                contentId={eventData?.id}
                                size="lg"
                                theme="light"
                                showLikedByText
                            />
                        </div>

                        {/* Details section in the header */}
                        <div className="bg-dark bg-opacity-50 rounded p-1 mt-auto">
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="text-white">
                                    <EventDetails
                                        eventData={{
                                            ...eventData,
                                            community_name: displayCommunityName // Pass combined community name
                                        }}
                                        theme="light"
                                        hideDateInDetails={true} // New prop to prevent showing date twice
                                    />
                                </div>
                                <div className="ms-1">
                                    <PermissionAwareEditButton
                                        item={eventData}
                                        onClick={() => setShowDetailsModal(true)}
                                        color="text-white"
                                        title={t('events.actions.editDetails')}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <div className="card-body">
               {/* Setlist Section - Only show if not a party event */}
               {!isPartyEvent && (
                    <div className="mb-4 setlist-wrapper" style={{ overflow: 'visible' }}>
                        <div className="d-flex align-items-center justify-content-between mb-1">
                            <div className="d-flex align-items-center">
                                <h5 className="mb-0">{t('events.setlist.title')}</h5>
                                <PermissionAwareEditButton
                                    item={eventData}
                                    onClick={() => setShowSetlistModal(true)}
                                    title={t('events.actions.editSetlist')}
                                />
                            </div>
                            {setlist && setlist.songs && setlist.songs.length > 0 && (
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleExportPPTX}
                                    disabled={exportingPPTX}
                                >
                                    <FontAwesomeIcon icon={faFileExport} className="me-1" />
                                    {exportingPPTX ? t('events.setlist.exporting') : t('events.setlist.exportPPTX')}
                                </Button>
                            )}
                        </div>
                        <EventSetlist 
                            loading={loading || loadingSongParts} 
                            setlist={setlist}
                            eventData={eventData}
                            songsWithParts={songsWithParts}
                            isMobile={false}
                            onUpdate={(updatedSetlist) => {
                                // Update the local setlist state
                                setSetlist(updatedSetlist);
                                
                                // Refresh the song parts data with the updated setlist
                                if (updatedSetlist && updatedSetlist.songs && updatedSetlist.songs.length > 0) {
                                    fetchSongPartsData(updatedSetlist.songs);
                                }
                            }}
                        />
                    </div>
                )}
                
                {/* Attendees Section */}
                <div className="mb-4">
                    <div className="d-flex align-items-center mb-1 justify-content-between">
                        <div className="d-flex align-items-center">
                            <h5 className="mb-0">{t('events.attendees.title')}</h5>
                            <PermissionAwareEditButton
                                item={eventData}
                                onClick={() => setShowAttendeesModal(true)}
                                title={t('events.actions.editAttendees')}
                            />
                        </div>
                        <button
                            className="btn btn-sm btn-outline-secondary d-flex align-items-center"
                            onClick={() => setAttendeesCollapsed(!attendeesCollapsed)}
                            aria-expanded={!attendeesCollapsed}
                            aria-controls="attendees-list"
                        >
                            <small>{attendeesCollapsed ? t('events.actions.showAll') : t('events.actions.showOnlyMe')}</small>
                            <FontAwesomeIcon
                                icon={attendeesCollapsed ? faChevronDown : faChevronUp}
                                className="ms-1"
                            />
                        </button>
                    </div>
                    <EventAttendees
                        eventData={eventData}
                        onEventDataUpdate={handleEventDataUpdate}
                        user={user}
                        showOnlyCurrentUser={attendeesCollapsed}
                    />
                </div>

                {/* Comments Section */}
                <div>
                    <div className="d-flex align-items-center mb-1 justify-content-between">
                        <h5 className="mb-0">{t('events.comments.title') || 'Comments'}</h5>
                        <button
                            className="btn btn-sm btn-outline-secondary d-flex align-items-center"
                            onClick={() => setCommentsCollapsed(!commentsCollapsed)}
                            aria-expanded={!commentsCollapsed}
                            aria-controls="comments-section"
                        >
                            <small>{commentsCollapsed ? t('events.actions.expand') || 'Expand' : t('events.actions.collapse') || 'Collapse'}</small>
                            <FontAwesomeIcon
                                icon={commentsCollapsed ? faChevronDown : faChevronUp}
                                className="ms-1"
                            />
                        </button>
                    </div>
                    <EventComments
                        eventData={eventData}
                        user={user}
                        collapsed={commentsCollapsed}
                    />
                </div>
            </div>
            
            {/* Event Edit Modals - Only render when shown */}
            {showDetailsModal && (
                <EventDetailsModal
                    event={eventData}
                    onClose={() => setShowDetailsModal(false)}
                    onSave={handleDetailsSaved}
                />
            )}
            
            {showAttendeesModal && (
                <EventAttendeesModal
                    event={eventData}
                    onClose={() => setShowAttendeesModal(false)}
                    onSave={handleAttendeesSaved}
                />
            )}
            
            {/* Only show setlist modal for non-party events */}
            {!isPartyEvent && showSetlistModal && (
                <EventSetlistModal
                    event={eventData}
                    setlist={setlist}
                    onClose={() => setShowSetlistModal(false)}
                    onSave={handleSetlistSaved}
                />
            )}

            {/* Header Image Modal */}
            {showHeaderImageModal && (
                <HeaderImageModal
                    show={showHeaderImageModal}
                    onHide={() => setShowHeaderImageModal(false)}
                    event={eventData}
                    onImageUpdate={handleHeaderImageUpdate}
                />
            )}

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal />
        </div>
    );
}

export default EventCard;